/*******************************************************************************

Copyright (c) 2020-2021, Visual Elements AS

******************************************************************************/

// @format

/** Namespace for DOM helper functions
 * @ignore
 */

import { isNull, isArr, isFn } from "./highcharts-editor";

  /** Append a set of nodes to another node.
     * Arguments supplied after the @param {} target represents the children to append.
     * @namespace highed.dom
     * @param target {object} - the node to append to
     * @return {domnode} - the target
     */
  export function ap(target) {
    let children = Array.prototype.slice.call(arguments);
    children.splice(0, 1);

    target = getNode(target);

    if (!isNull(target) && typeof target.appendChild !== 'undefined') {
      children.forEach(function(child) {
        if (isArr(child)) {
          child.forEach(function(sc) {
            ap(target, sc);
          });
        } else if (
          typeof child !== 'undefined' &&
          typeof child.appendChild !== 'undefined'
        ) {
          target.appendChild(child);
        } else if (child !== false) {
          console.log(1, 'child is not valid (ap)');
        }
      });
    } else {
      console.log(1, 'target is not a valid DOM node (ap)');
    }

    return target;
  };

  /** Create a new HTML node
     * @namespace highed.dom
     * @param type {string} - the type of node to create
     * @param cssClass {string} (optional) - the css class to use for the node
     * @param innerHTML {string} (optional) - the inner html of the new node
     * @param id {string} (optional) - the id of the new node
     *
     * @return {domnode} - the new dom node
     */
  export function cr(type, cssClass, innerHTML, id) {
    let res = false;

    if (typeof type !== 'undefined') {
      res = document.createElement(type);

      if (typeof cssClass !== 'undefined') {
        res.className = cssClass;
      }
      if (typeof innerHTML !== 'undefined' && typeof innerHTML !== 'object') {
        res.innerHTML = innerHTML;
      }

      if (typeof id !== 'undefined') {
        res.id = id;
      }
    } else {
      console.log(1, 'no node type supplied (cr');
    }

    return res;
  };

  /** Style a node
     * @namespace highed.dom
     * @param nodes {(object|array)}  - the node to style. Can also be an array
     * @param style {object} - object containing style properties
     *
     * @return {anything} - whatever was supplied to @param {} nodes
     */
  export function setStyle(nodes, styling) {
    if (isArr(nodes)) {
      nodes.forEach(function(node) {
        setStyle(node, styling);
      });
      return nodes;
    }

    if (nodes && nodes.style) {
      Object.keys(styling).forEach(function(p) {
        nodes.style[p] = styling[p];
      });
      return nodes;
    }
    return false;
  };

  /** Attach an event listener to a dom node
     * @namespace highed.dom
     * @param target {object} - the dom node to attach to
     * @param event {string} - the event to listen for
     * @param callback {function} - the function to call when the event is emitted
     * @param context {object} (optional) - the context of the callback function
     *
     * @return {function} - a function that can be called to unbind the handler
     */
  export function on(target, event, callback, context) {
    let s = [];

    if (!target) {
      return function() {};
    }

    if (isArr(event)) {
      event.forEach(function(event) {
        s.push(on(target, event, callback, context));
      });

      return function() {
        s.forEach(function(f) {
          f();
        });
      };
    }

    if (target === document.body && event === 'resize') {
      //Need some special magic here eventually.
    }

    if (target && target.forEach) {
      target.forEach(function(t) {
        s.push(on(t, event, callback));
      });
    }

    if (s.length > 0) {
      return function() {
        s.forEach(function(f) {
          f();
        });
      };
    }

    function actualCallback() {
      if (isFn(callback)) {
        return callback.apply(context, arguments);
      }
      return;
    }

    if (target.addEventListener) {
      target.addEventListener(event, actualCallback, false);
    } else {
      target.attachEvent('on' + event, actualCallback, false);
    }

    return function() {
      if (window.removeEventListener) {
        target.removeEventListener(event, actualCallback, false);
      } else {
        target.detachEvent('on' + event, actualCallback);
      }
    };
  };

  /** Find a node
     * @namespace highed.dom
     * @param node {object} - the node to find. Either a string or an actual node instance
     * @return {object} - the node or false if the node was not found
     */
  export function getNode(node) {
    if (node && node.appendChild) {
      return node;
    }
    return document.getElementById(node) || false;
  };
